// frontend/src/lib/domains/accounts.ts
import { apiGet, apiPost } from "../core/request";

export type AccountSummary = {
  id: number | string;
  currency: string;
  balance?: number | null;
  available_balance?: number | null;
  iban?: string | null;
  label?: string | null;
  plan_label?: string | null;
};

export type AccountsIndexResponse =
  | { accounts?: AccountSummary[] }
  | { data?: AccountSummary[] }
  | AccountSummary[];

/**
 * Safely unwrap list responses that might be:
 * - [ ... ]
 * - { accounts: [ ... ] }
 * - { data: [ ... ] }
 */
function unwrapAccounts(res: AccountsIndexResponse | any): AccountSummary[] {
  if (!res) return [];

  if (Array.isArray(res)) {
    return res as AccountSummary[];
  }

  if (Array.isArray((res as any).accounts)) {
    return (res as any).accounts as AccountSummary[];
  }

  if (Array.isArray((res as any).data)) {
    return (res as any).data as AccountSummary[];
  }

  return [];
}

/**
 * Normalized helper: always returns AccountSummary[] regardless of envelope.
 * Used by SPA pages (Transfers, Accounts, etc).
 */
export async function apiAccountsIndex(): Promise<AccountSummary[]> {
  const res = await apiGet<AccountsIndexResponse>("/xapi/accounts");
  return unwrapAccounts(res);
}

/**
 * Legacy/raw helper – kept for compatibility.
 * Callers that still expect the raw JSON envelope can keep using this.
 */
export function apiAccounts<T = any>() {
  return apiGet<T>("/xapi/accounts");
}

export function apiAccountRequestOptions<T = any>() {
  return apiGet<T>("/xapi/accounts/request-options");
}

export function apiAccountsAvailableCurrencies<T = any>() {
  // Kept for compatibility; server now prefers request-options (plan-aware).
  return apiGet<T>("/xapi/accounts/request-options");
}

export function apiAccountPlans<T = any>(id: string | number) {
  return apiGet<T>(`/xapi/accounts/${id}/plans`);
}

export function apiRequestAccount<T = any>(payload: any) {
  // expects: { plan_id, currency }
  return apiPost<T>("/xapi/accounts/request", payload);
}

export function apiChangeAccountPlan<T = any>(
  id: string | number,
  payload: any
) {
  return apiPost<T>(`/xapi/accounts/${id}/change-plan`, payload);
}
